import { useState, useEffect, useCallback, useRef } from 'react';
import type { FlushResult } from './useHeadcountPendingSync';
import type { SaveBeforeLeaveState } from '../Components/Headcount/HeadcountSaveBeforeLeaveDialog';
import { useNavigationGuard } from '../Contexts/NavigationGuardContext';

export interface UseHeadcountNavigationGuardOptions {
  hasPendingChanges: boolean;
  isSyncing: boolean;
  isOnline: boolean;
  flushPendingStatusUpdates: (reason: 'navigation' | 'retry') => Promise<FlushResult>;
  cancelDelayedSync: () => void;
  discardPendingStatusUpdates: () => void;
}

export const useHeadcountNavigationGuard = (options: UseHeadcountNavigationGuardOptions) => {
  const {
    hasPendingChanges,
    isSyncing,
    isOnline,
    flushPendingStatusUpdates,
    cancelDelayedSync,
    discardPendingStatusUpdates,
  } = options;

  const { registerGuard, unregisterGuard, proceedNavigation, cancelNavigation } = useNavigationGuard();

  const [dialogState, setDialogState] = useState<SaveBeforeLeaveState>('idle');
  const [dialogError, setDialogError] = useState<string | undefined>(undefined);
  const shouldBlock = hasPendingChanges || isSyncing;

  // Keep refs for values needed in the guard callback
  const shouldBlockRef = useRef(shouldBlock);
  shouldBlockRef.current = shouldBlock;
  const isOnlineRef = useRef(isOnline);
  isOnlineRef.current = isOnline;

  // Register/unregister the navigation guard
  useEffect(() => {
    const guard = (_destination: string): boolean => {
      if (!shouldBlockRef.current) return true; // allow

      // Block navigation and start flush
      if (!isOnlineRef.current) {
        setDialogState('offline');
        setDialogError(undefined);
        return false;
      }

      setDialogState('saving');
      setDialogError(undefined);
      cancelDelayedSync();

      flushPendingStatusUpdates('navigation').then((result) => {
        if (result.success) {
          setDialogState('idle');
          proceedNavigation();
        } else {
          setDialogState('error');
          setDialogError(result.error);
        }
      });

      return false; // block — will call proceedNavigation/cancelNavigation later
    };

    registerGuard(guard);
    return () => unregisterGuard();
  }, [registerGuard, unregisterGuard, cancelDelayedSync, flushPendingStatusUpdates, proceedNavigation]);

  // ── beforeunload guard ──
  useEffect(() => {
    if (!shouldBlock) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [shouldBlock]);

  // ── Dialog actions ──
  const handleRetry = useCallback(() => {
    setDialogState('saving');
    setDialogError(undefined);
    flushPendingStatusUpdates('retry').then((result) => {
      if (result.success) {
        setDialogState('idle');
        proceedNavigation();
      } else {
        setDialogState('error');
        setDialogError(result.error);
      }
    });
  }, [flushPendingStatusUpdates, proceedNavigation]);

  const handleStay = useCallback(() => {
    setDialogState('idle');
    setDialogError(undefined);
    cancelNavigation();
  }, [cancelNavigation]);

  const handleDiscard = useCallback(() => {
    discardPendingStatusUpdates();
    setDialogState('idle');
    setDialogError(undefined);
    proceedNavigation();
  }, [discardPendingStatusUpdates, proceedNavigation]);

  return {
    showDialog: dialogState !== 'idle',
    dialogState,
    dialogError,
    handleRetry,
    handleStay,
    handleDiscard,
  };
};
